import type { LoaderFunctionArgs, MetaFunction } from "react-router";
import { Link, useLoaderData, data } from "react-router";
import invariant from "tiny-invariant";
import { JumpToSection } from "~/components/jump-to-section";
import { QuickInfoSection } from "~/components/quick-info";
import { Section } from "~/components/section";
import { AdditionalInformationSection } from "~/components/sections/additional";
import { AuthoritySection } from "~/components/sections/authority";
import { ComplaintsSection } from "~/components/sections/complaints";
import { FleetSection } from "~/components/sections/fleet";
import { InspectionsSection } from "~/components/sections/inspections";
import { InsuranceSection } from "~/components/sections/insurance";
import { SafetySection } from "~/components/sections/safety";
import { getBusiness } from "~/data/.server/business";
import { getCoordinates } from "~/data/.server/nominatim";
import stateConverter from "us-state-converter";
import {
  capitalizeName,
  cn,
  extractDotFromSlug,
  getSuccess,
  isFailure,
  unwrap,
} from "~/lib/utils";
import {
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon,
} from "@radix-ui/react-icons";
import { buttonVariants } from "~/components/ui/button";

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const state = data?.business.state;
  const name = data?.business.company?.basic?.name;
  invariant(name, "No business name provided");
  invariant(state, "No state provided");

  const allowedToOperate = data?.business.carrierInfo?.detail?.allowedToOperate;
  const { trucks } = data?.business.company;
  const drivers = data?.business.carrierInfo?.detail?.totalDrivers;
  const totalComplaints = data?.business.company.complaints?.reduce(
    (c, total) => c + (total?.count ?? 0),
    0,
  );
  const carrierOperationDesc =
    data?.business?.carrierInfo?.detail?.carrierOperation?.carrierOperationDesc;
  const dot = data?.business.company.dot;
  const mc = data?.business.company.mc;
  return [
    { title: `${capitalizeName(name)} in ${state} | MovingData.org` },
    {
      name: "description",
      content: `${capitalizeName(name)} is a local moving company in ${state} authorized for ${carrierOperationDesc} transport. They have a fleet of ${trucks} trucks and ${drivers} drivers. They have a total of ${totalComplaints} complaints filed against them. They are authorized to operate under USDOT ${dot} and MC ${mc}.`,
    },
  ];
};

export const loader = async ({ params, context }: LoaderFunctionArgs) => {
  const db = context.cloudflare.env.DB;
  invariant(params.state, "No state provided");
  invariant(params.slug, "Invalid business slug provided");
  const dot = getSuccess(extractDotFromSlug(params.slug));
  invariant(dot, "Invalid business slug provided");
  const business = await getBusiness(db, dot).then(unwrap);
  const coords = await getCoordinates(business.company?.address!);

  return data(
    { business, coords },
    { headers: { "Cache-Control": "public, s-maxage=60" } },
  );
};

function BreadcrumbItem({
  href,
  children,
  showArrow = true,
}: {
  href: string;
  children: React.ReactNode;
  showArrow?: boolean;
}) {
  return (
    <li className="">
      <Link className="flex items-center gap-1 hover:text-primary" to={href}>
        <span className="block">{children}</span>{" "}
        {showArrow && (
          <DoubleArrowRightIcon className="flex size-4 items-center justify-center text-primary" />
        )}
      </Link>
    </li>
  );
}

export default function App() {
  const { business, coords } = useLoaderData<typeof loader>();
  let name = business.company?.basic?.name;
  name = name ? capitalizeName(name) : name;

  return (
    <article className="prose-sm relative mx-auto max-w-screen-lg space-y-0 px-2 pt-5 dark:prose-invert prose-h2:mt-0">
      <div className="relative mb-5">
        <h1 className="mb-2">{name}</h1>
        <div>
          <ul className="not-prose flex flex-wrap items-center justify-start gap-2">
            <BreadcrumbItem href="/all-moving-companies">
              All Companies
            </BreadcrumbItem>
            <BreadcrumbItem href={`/state/${business.state.toLowerCase()}`}>
              {stateConverter.fullName(business.state)}
            </BreadcrumbItem>
            <BreadcrumbItem
              showArrow={false}
              href={`/state/${business.state.toLowerCase()}/business/${business.slug}`}
            >
              {capitalizeName(name ?? "")}
            </BreadcrumbItem>
          </ul>
        </div>
        <div className="block md:hidden py-2">Is this business yours? </div>
        <span className="bottom-0 right-2 flex flex-row items-center justify-start gap-2 text-center md:absolute">
          <div className="hidden md:block">Is this business yours? </div>
          <Link
            to={`https://airtable.com/appIHktZDbHeSWXZw/pagUF8VrWYvxOkBEN/form?prefill_Business+Name=${encodeURIComponent(capitalizeName(name ?? ""))}`}
            className={buttonVariants()}
          >
            Claim your business.
          </Link>
          <Link
            to={`https://airtable.com/appIHktZDbHeSWXZw/pagMvMy8SyatVZehv/form?prefill_Business+Name=${encodeURIComponent(capitalizeName(name ?? ""))}`}
            className={cn(
              buttonVariants({ variant: "secondary" }),
              "block bg-foreground text-background",
            )}
          >
            Request Edits
          </Link>
        </span>
      </div>

      <Section className="pb-2" noHr id="quick-info">
        <QuickInfoSection
          latitude={parseFloat(coords.lat)}
          longitude={parseFloat(coords.lon)}
          business={business}
        />
      </Section>

      <div className="not-prose sticky top-0 w-full border-b bg-white px-2 py-2">
        <JumpToSection
          sections={[
            { id: "fleet", title: "Fleet" },
            { id: "safety", title: "Safety" },
            { id: "insurance", title: "Insurance" },
            { id: "authority", title: "Authority" },
            { id: "complaints", title: "Complaints" },
            { id: "inspections", title: "Inspections" },
            { id: "more", title: "More" },
          ]}
        />
      </div>
      <div>
        <Section id="fleet" title="Fleet">
          <FleetSection business={business} />
        </Section>
        <Section id="safety" title="Safety">
          <SafetySection business={business} />
        </Section>
        <Section id="insurance" title="Insurance">
          <InsuranceSection business={business} />
        </Section>
        <Section id="authority" title="Authority">
          <AuthoritySection business={business} />
        </Section>
        <Section id="complaints" title="Complaints">
          <ComplaintsSection business={business} />
        </Section>
        <Section id="inspections" title="Inspections and Crash Data">
          <InspectionsSection business={business} />
        </Section>
        <Section id="more" title="Additional Information">
          <AdditionalInformationSection business={business} />
        </Section>
      </div>
    </article>
  );
}
